import { safeLocalStorage } from 'utils/safeStorage';
// Helpers for the `booking.bizly.com` marketing alias, which serves the pre-login
// Discover experience at clean paths and hands users back to the main platform domain.

// Matches any host whose subdomain is `booking` (e.g. booking.bizly.com, dev.booking.bizly.com).
const BOOKING_HOST_PATTERN = /(^|\.)booking\./;

// Non-prod QA escape hatch. There is no hosted dev booking host — a Netlify domain alias can
// only serve the production deploy — so allow forcing booking mode on the dev platform host
// (e.g. dev.platform.bizly.com) or locally. Toggle with `?booking=1` / `?booking=0`, which
// persists to localStorage so it survives SPA navigation. Never active in prod.
const BOOKING_OVERRIDE_KEY = 'forceBooking';

const hasBookingOverride = (): boolean => {
    if (import.meta.env.VITE_APP_ENV === 'prod') return false;
    const param = new URLSearchParams(window.location.search).get('booking');
    if (param === '1') safeLocalStorage.set(BOOKING_OVERRIDE_KEY, '1');
    if (param === '0') safeLocalStorage.remove(BOOKING_OVERRIDE_KEY);
    return safeLocalStorage.get(BOOKING_OVERRIDE_KEY) === '1';
};

// Drops the persisted `?booking=1` QA override. For override users the platform origin is the
// current host, so the booking alias's "hand authenticated visitors back to the platform" redirect
// would loop forever — clearing the override is its equivalent.
export const clearBookingOverride = (): void => safeLocalStorage.remove(BOOKING_OVERRIDE_KEY);

// True only on a real booking.* host, regardless of the QA override.
export const isBookingHost = (hostname: string = window.location.hostname): boolean =>
    BOOKING_HOST_PATTERN.test(hostname);

export const isBookingDomain = (hostname: string = window.location.hostname): boolean =>
    hasBookingOverride() || isBookingHost(hostname);

// True on the anonymous, pre-login Discover surfaces where a missing/expired auth token is
// expected and API 401s must NOT bounce the visitor to /sign-in. Two shapes: the platform
// Discover paths (/discover*) and the entire booking.bizly.com alias, which serves the same
// Discover experience at clean paths (/ and /explore-venues). On the booking alias a /sign-in
// redirect dead-ends at the catch-all route and sends the user back to the booking home page.
export const isAnonymousDiscoverSurface = (pathname: string = window.location.pathname): boolean =>
    pathname.startsWith('/discover') || isBookingDomain();

// The canonical platform origin a booking-alias user is sent to after login.
export const getPlatformOrigin = (): string =>
    import.meta.env.VITE_APP_ENV === 'prod' ? 'https://platform.bizly.com' : 'https://dev.platform.bizly.com';

type DiscoverPaths = { home: string; explore: string };

// The Discover route paths differ per surface: clean paths on the booking alias,
// `/discover`-prefixed paths on the platform domain.
export const getDiscoverPaths = (): DiscoverPaths =>
    isBookingDomain()
        ? { home: '/', explore: '/explore-venues' }
        : { home: '/discover', explore: '/discover/explore-venues' };
